import { ChangeEvent, RefObject } from 'react';
import styled from 'styled-components';

import { DefaultButtonStyled } from '@/components/atom/buttons/DefaultButton';
import Delete from '@/components/atom/icon/Delete';
import Plus from '@/components/atom/icon/Plus';
import SecurityShieldProfile from '@/components/atom/icon/SecurityShieldProfile';
import Loader from '@/components/molecules/Loader';
import theme from '@/styles/theme';
import { FileWithBlobUrl } from '@/types/common';

export interface QnaWriteImagesProps {
  isUploadingImage: boolean;
  questionImages: FileWithBlobUrl[];
  imageInputRef: RefObject<HTMLInputElement>;
  onDeleteImage: (idx: number) => void;
  onUploadImage: () => void;
  onHandleChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

const QnaWriteImages = ({
  isUploadingImage,
  questionImages,
  imageInputRef,
  onDeleteImage,
  onUploadImage,
  onHandleChange,
}: QnaWriteImagesProps) => {
  return (
    <QuickAnswerWriteImagesFieldWrapper>
      <ImagesFieldLabel>사진 첨부 [선택]</ImagesFieldLabel>
      <ImagesFieldLabelDescription>
        증상과 관련된 사진을 최대 4장까지 첨부할 수 있어요.
      </ImagesFieldLabelDescription>
      <ImagesWrapper>
        {!!questionImages.length &&
          questionImages.map((image, idx) => (
            <QuestionImageWrapper
              key={`question_image_${idx}`}
              isMargin={idx !== 3}
            >
              <QuestionImageDeleteButton
                type={'button'}
                onClick={() => onDeleteImage(idx)}
              >
                <Delete size={12} />
              </QuestionImageDeleteButton>
              <QuestionImage
                alt="첨부이미지"
                width="72"
                height="72"
                src={image.blobUrl}
              />
            </QuestionImageWrapper>
          ))}
        {questionImages.length < 4 && (
          <EmptyImageBox type={'button'} onClick={() => onUploadImage()}>
            {isUploadingImage ? (
              <Loader />
            ) : (
              <Plus color={theme.color.gray600} width={16} height={16} />
            )}
          </EmptyImageBox>
        )}
        <input
          ref={imageInputRef}
          type={'file'}
          multiple
          onChange={onHandleChange}
          accept="image/*"
          style={{ display: 'none' }}
        />
      </ImagesWrapper>

      <QuestionImageInfomation>
        <ImageInfomationTitle>
          <SecurityShieldProfile />
          안심하세요!
        </ImageInfomationTitle>
        <ImageInfomationContent>
          개인정보노출의 위험이 있는 사진은 작성자와 의료진에게만 보일 수 있도록
          처리해드려요.
        </ImageInfomationContent>
      </QuestionImageInfomation>
    </QuickAnswerWriteImagesFieldWrapper>
  );
};

export default QnaWriteImages;

const QuickAnswerWriteImagesFieldWrapper = styled.div`
  padding: 40px 0;
`;

const ImagesFieldLabel = styled.div`
  ${({ theme }) => `
    ${theme.typography.UI14SB};
    color: ${theme.color.gray700};
    `};
`;

const ImagesFieldLabelDescription = styled.div`
  margin-top: 8px;

  ${({ theme }) => `
    ${theme.typography.UI14M};
    color: ${theme.color.gray400};
    `};
`;

const ImagesWrapper = styled.div`
  display: flex;
  padding: 24px 0;
  overflow: scroll;
`;

const EmptyImageBox = styled(DefaultButtonStyled)`
  width: 72px;
  height: 72px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;

  ${({ theme }) => `
    background: ${theme.color.gray20};
    border: 1px solid ${theme.color.gray200};

    &:active {
    background: ${theme.color.gray200};
    }
    `};
`;

const QuestionImageWrapper = styled.div<{ isMargin: boolean }>`
  position: relative;

  ${({ isMargin }) => isMargin && 'margin-right: 16px'};
`;

const QuestionImage = styled.img`
  border-radius: 8px;
  object-fit: cover;

  ${({ theme }) => `
    border: 1px solid ${theme.color.gray200};
  `};
`;

const QuestionImageDeleteButton = styled(DefaultButtonStyled)`
  position: absolute;
  width: 24px;
  height: 24px;
  top: 4px;
  right: 4px;
  border-radius: 12px;
  background: ${({ theme }) => theme.color.dimmed80};
`;

const QuestionImageInfomation = styled.div`
  padding: 16px;
  border-radius: 8px;
  background: ${({ theme }) => theme.color.blue100};
`;

const ImageInfomationTitle = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 4px;

  ${({ theme }) => `
    ${theme.typography.UI14SB};
    color: ${theme.color.blue500};
  `};

  svg {
    margin-right: 8px;
  }
`;

const ImageInfomationContent = styled.div`
  margin-left: 26px;

  ${({ theme }) => `
    ${theme.typography.UI14M};
    color: ${theme.color.gray500};
  `};
`;
